import { useCallback, useEffect, useState } from "react";
import { Button, EmptyState, Icon, IconButton, Input, Modal, Pill, Progress, Select, Textarea, useToast } from "@astrum/ui";
import type { PillTone } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import type { Milestone, MilestoneStatus } from "../lib/api.js";
import type { InfoPanelMember } from "./InfoPanel.js";
import "./project-milestones.css";

/**
 * A project's milestones: dated checkpoints with an owner, and a progress bar
 * read from the tasks linked to each. Editors can add, change and remove them;
 * everyone who can read the project sees the list.
 */

const STATUS_LABEL: Record<MilestoneStatus, string> = {
  planned: "Planned",
  at_risk: "At risk",
  done: "Done",
  missed: "Missed",
};

const STATUS_TONE: Record<MilestoneStatus, PillTone> = {
  planned: "neutral",
  at_risk: "warn",
  done: "ok",
  missed: "danger",
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

interface Props {
  projectId: string;
  members: InfoPanelMember[];
  canEdit: boolean;
  /** Called after a change so the page can refresh anything derived from milestones. */
  onChanged?: () => void;
}

export function ProjectMilestones({ projectId, members, canEdit, onChanged }: Props) {
  const { toast } = useToast();
  const [milestones, setMilestones] = useState<Milestone[] | null>(null);
  const [editing, setEditing] = useState<Milestone | "new" | null>(null);
  const [deleting, setDeleting] = useState<Milestone | null>(null);

  const load = useCallback(() => {
    api
      .get<{ milestones: Milestone[] }>(`/projects/${projectId}/milestones`)
      .then((res) => setMilestones(res.milestones))
      .catch(() => setMilestones([]));
  }, [projectId]);

  useEffect(load, [load]);

  const changed = () => {
    load();
    onChanged?.();
  };

  const next = milestones?.find((m) => m.status === "planned" || m.status === "at_risk");

  return (
    <section className="project-detail__section project-milestones">
      <div className="project-detail__section-head">
        <h2>Milestones</h2>
        <div className="project-detail__section-actions">
          {canEdit && (
            <Button size="sm" variant="secondary" icon={<Icon name="plus" size={13} />} onClick={() => setEditing("new")}>
              Add milestone
            </Button>
          )}
        </div>
      </div>

      {milestones === null ? null : milestones.length === 0 ? (
        <EmptyState
          icon="flag"
          title="No milestones yet"
          description="Milestones mark the dates the project is measured against."
        />
      ) : (
        <ul className="project-milestones__list">
          {milestones.map((m) => (
            <li key={m.id} className={`project-milestones__item ${m === next ? "is-next" : ""}`}>
              <div className="project-milestones__head">
                <span className="project-milestones__name">{m.name}</span>
                {m === next && <Pill tone="accent">Next</Pill>}
                <Pill tone={STATUS_TONE[m.status]}>{STATUS_LABEL[m.status]}</Pill>
                {canEdit && (
                  <span className="project-milestones__actions">
                    <IconButton icon={<Icon name="edit" size={14} />} label={`Edit ${m.name}`} onClick={() => setEditing(m)} />
                    <IconButton icon={<Icon name="trash" size={14} />} label={`Delete ${m.name}`} onClick={() => setDeleting(m)} />
                  </span>
                )}
              </div>
              <div className="project-milestones__meta">
                <span>{formatDate(m.dueDate)}</span>
                {m.ownerName && <span>{m.ownerName}</span>}
                <span>{m.taskCount === 0 ? "No tasks linked" : `${m.doneCount}/${m.taskCount} tasks done`}</span>
              </div>
              {m.description && <p className="project-milestones__desc">{m.description}</p>}
              {m.progress !== null && (
                <Progress value={m.progress / 100} label={`${m.name} progress`} compact tone={m.status === "done" ? "ok" : "accent"} />
              )}
            </li>
          ))}
        </ul>
      )}

      <Modal open={editing !== null} onClose={() => setEditing(null)} title={editing === "new" ? "New milestone" : "Edit milestone"}>
        {editing && (
          <MilestoneForm
            key={editing === "new" ? "new" : editing.id}
            projectId={projectId}
            milestone={editing === "new" ? null : editing}
            members={members}
            onSaved={() => {
              setEditing(null);
              changed();
            }}
            onCancel={() => setEditing(null)}
          />
        )}
      </Modal>

      <Modal open={deleting !== null} onClose={() => setDeleting(null)} title="Delete milestone">
        {deleting && (
          <div className="project-milestones__confirm">
            <p>
              Delete <strong>{deleting.name}</strong>? Tasks linked to it stay, and simply lose the link.
            </p>
            <div className="project-milestones__form-actions">
              <Button variant="ghost" onClick={() => setDeleting(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  api
                    .delete(`/milestones/${deleting.id}`)
                    .then(() => {
                      setDeleting(null);
                      changed();
                      toast("Milestone deleted.");
                    })
                    .catch((e: unknown) => toast(e instanceof ApiError ? e.message : "Could not delete that milestone.", { tone: "danger" }));
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </section>
  );
}

function MilestoneForm({
  projectId,
  milestone,
  members,
  onSaved,
  onCancel,
}: {
  projectId: string;
  milestone: Milestone | null;
  members: InfoPanelMember[];
  onSaved: () => void;
  onCancel: () => void;
}) {
  const { toast } = useToast();
  const [name, setName] = useState(milestone?.name ?? "");
  const [description, setDescription] = useState(milestone?.description ?? "");
  const [ownerId, setOwnerId] = useState(milestone?.ownerId ?? "");
  // A calendar date carries no timezone; it goes up as midnight UTC.
  const [dueDate, setDueDate] = useState(milestone?.dueDate.slice(0, 10) ?? "");
  const [status, setStatus] = useState<MilestoneStatus>(milestone?.status ?? "planned");
  const [saving, setSaving] = useState(false);

  const submit = () => {
    if (!name.trim() || !dueDate) return;
    setSaving(true);
    const body = {
      name: name.trim(),
      description: description.trim() || null,
      ownerId: ownerId || null,
      dueDate: `${dueDate}T00:00:00.000Z`,
      status,
    };
    const request = milestone
      ? api.patch(`/milestones/${milestone.id}`, { ...body, version: milestone.version })
      : api.post(`/projects/${projectId}/milestones`, body);
    request
      .then(onSaved)
      .catch((error: unknown) => {
        const message =
          error instanceof ApiError && error.isConflict
            ? "Someone else changed this milestone. Reload and try again."
            : error instanceof ApiError
              ? error.message
              : "Could not save that milestone.";
        toast(message, { tone: "danger" });
      })
      .finally(() => setSaving(false));
  };

  return (
    <form
      className="project-milestones__form"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} autoFocus required />
      <div className="project-milestones__form-row">
        <Input label="Date" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} required />
        <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value as MilestoneStatus)}>
          {(Object.keys(STATUS_LABEL) as MilestoneStatus[]).map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </Select>
      </div>
      <Select label="Owner" value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
        <option value="">No owner</option>
        {members.map((member) => (
          <option key={member.id} value={member.id}>
            {member.displayName}
          </option>
        ))}
      </Select>
      <Textarea label="Description" value={description} maxRows={4} onChange={(e) => setDescription(e.target.value)} />
      <div className="project-milestones__form-actions">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={saving} disabled={!name.trim() || !dueDate}>
          {milestone ? "Save" : "Add milestone"}
        </Button>
      </div>
    </form>
  );
}
